/**
 * 공유 유틸 — 시간/전화번호/표시 포맷. 서버·클라이언트 공통.
 */
import type { Message, PublicUser, RoomListItem } from './types';

/** E.164 비슷하게 정규화 (한국 기준: 0으로 시작하면 +82) */
export function normalizePhone(input: string): string {
  const digits = input.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('0')) return '+82' + digits.slice(1);
  return digits;
}

export function isValidPhone(input: string): boolean {
  const n = normalizePhone(input);
  return /^\+?\d{9,15}$/.test(n);
}

/** 카톡식 시각: 오전/오후 h:mm */
export function formatKakaoTime(ts: number): string {
  const d = new Date(ts);
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h < 12 ? '오전' : '오후';
  h = h % 12;
  if (h === 0) h = 12;
  return `${ampm} ${h}:${m}`;
}

/** 채팅 목록의 시각: 오늘이면 시간, 어제, 그 외 날짜 */
export function formatListTime(ts: number, now = Date.now()): string {
  const d = new Date(ts);
  const today = new Date(now);
  const isSameDay = d.toDateString() === today.toDateString();
  if (isSameDay) return formatKakaoTime(ts);
  const yesterday = new Date(now - 86400000);
  if (d.toDateString() === yesterday.toDateString()) return '어제';
  if (d.getFullYear() === today.getFullYear()) {
    return `${d.getMonth() + 1}월 ${d.getDate()}일`;
  }
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

/** 날짜 구분선: 2026년 6월 25일 목요일 */
export function formatDateDivider(ts: number): string {
  const d = new Date(ts);
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${days[d.getDay()]}요일`;
}

export function isSameDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** 메시지 미리보기 텍스트 (채팅 목록용) */
export function messagePreview(m: Message | null): string {
  if (!m) return '';
  if (m.deleted) return '삭제된 메시지입니다.';
  switch (m.type) {
    case 'image':
      return '사진';
    case 'file':
      return '파일';
    case 'call':
      return '통화';
    case 'system':
      return safeContent(m.content);
    default:
      return m.content;
  }
}

function safeContent(c: string): string {
  try {
    const o = JSON.parse(c);
    return typeof o?.text === 'string' ? o.text : c;
  } catch {
    return c;
  }
}

/** direct 방의 상대 또는 group 표시명 */
export function roomDisplayName(item: RoomListItem, myId: string): string {
  if (item.room.type === 'group') {
    return item.room.name || item.members.map((m) => m.name).join(', ');
  }
  const other = item.members.find((m) => m.id !== myId);
  return other?.name ?? item.displayName ?? '알 수 없음';
}

export function initials(name: string): string {
  return name.trim().slice(0, 2);
}

export function avatarColor(seed: string): string {
  const palette = ['#FBCFE8', '#BFDBFE', '#BBF7D0', '#FDE68A', '#DDD6FE', '#FCA5A5', '#A7F3D0'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

export function publicUser(name = '', id = ''): PublicUser {
  return { id, name, statusMessage: '', avatarUrl: null, department: null, position: null };
}

/** 간단 uuid (crypto 사용) */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
